import type { ReactNode } from "react";

/** The ONE way a panel's commit actions are laid out: at the foot, on the RIGHT.
 *
 *  Dattatray: *"I think there is inconsistency in the button placement on UI.
 *  We should follow that button should be placed on the right side of the UI
 *  for consistent user experience. I see some boxes have button on the left
 *  some boxes on right. The size of the buttons are also not consistent across
 *  activities."* Then, settling both: *"I want the buttons on right!"* and
 *  *"The size of the button in registry subpages is perfect — Enroll,
 *  Transfer, Ship etc."*
 *
 *  IT WAS A COIN FLIP, and that is not a figure of speech — measured across
 *  `ui/src` on 2026-09-29, of the rows carrying a commit action (`btn teal`)
 *  **18 were right-aligned and 18 were left**. Nobody chose either; each row
 *  was laid out by whoever wrote that panel, and the console ended up with two
 *  conventions and no rule.
 *
 *  THIS IS THE REGISTRY ROW, COPIED EXACTLY, because he named it as the one
 *  that is right — `ShipPage`, `TransferPage` and the enrol panel all render
 *
 *      display:flex · gap:10 · alignItems:center · justifyContent:flex-end
 *      marginTop:14 · a FULL-SIZE `btn teal`
 *
 *  so that is what this is. Not "inspired by": the same numbers, in one place,
 *  so the pages that already look right do not change at all and every other
 *  page arrives at them.
 *
 *  SIZE. Buttons in here are FULL size — no `sm`. `sm` is for an action ON A
 *  ROW (a table cell, a grid line, a chip strip), where a full-size control
 *  would blow the row height apart. The split is the question each answers:
 *  *finish this panel* versus *do something to this one line*. Products,
 *  Organisations and Asset Catalog had been using `sm` for both, which is why
 *  a Save there looked smaller than a Ship.
 *
 *  ORDER: the affirmative action goes LAST, hard against the edge, with the
 *  ways out (Cancel, Discard) to its left — the thing you meant to do is where
 *  the pointer already is, and the destructive neighbour is not.
 *
 *  COLOUR IS CONSEQUENCE, not prominence. See `ui/src/tokens/components.css`
 *  and the table in `tests/ui/test_buttons_sit_on_the_right.py`:
 *
 *      btn teal    commits what you intended        Save · Ship · Enroll
 *      btn red     throws work away or removes      Discard · Delete · Revoke
 *      btn ghost   everything else                  Cancel · Rename · Expand
 *
 *  Dattatray: *"buttons like discard shall be red or similar colour right!"*
 *  He was right and it was worse than he could see — measured the same day,
 *  EVERY destructive control in the console was `btn ghost sm`: Discard,
 *  Delete draft, Remove, Revoke, Archive, Uninstall, Stop. Twenty-five of
 *  them, none coloured, all identical to Cancel and to Rename.
 *
 *  A COMPONENT AND NOT A CONVENTION, because a convention is what produced
 *  18/18. `tests/ui/test_buttons_sit_on_the_right.py` fails on a hand-rolled
 *  action row, so the next one cannot drift.
 */
export function ActionRow({ children, top = 14 }: {
  /** notes and buttons, in reading order; the affirmative button LAST */
  children: ReactNode;
  /** space above, for a row that follows a dense control rather than a form */
  top?: number;
}) {
  return (
    <div style={{
      display: "flex", gap: 10, alignItems: "center",
      justifyContent: "flex-end", flexWrap: "wrap", marginTop: top,
    }}>
      {children}
    </div>
  );
}
